import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { useAuth } from '@/hooks/useAuth';
import { useResultSheet, useSaveResult, useVerifyResult, useReopenResult } from './hooks';
import { AbnormalFlag, Field, LabStatusBadge, fmtDateTime, isCriticalFlag } from './ui';
import type { LabDataType, ResultParameter } from './types';

/**
 * Structured result entry — one input per parameter, never one text box.
 *
 * ── What this screen deliberately does NOT do ────────────────────
 *
 * It does not decide whether a value is normal. The flag, the reference range
 * and the critical thresholds all come back from the server, computed against
 * THIS patient's age and sex by utils/labReferenceRange.js. A browser that
 * judged its own results could be made to judge them differently, and two
 * clients would eventually disagree about the same number.
 *
 * So the flow is: type a value → save → the server evaluates → the flags you
 * see are the flags that were stored. The flag column is empty until the
 * first save, and says so, rather than guessing ahead of the server.
 */

/** The choices a qualitative parameter offers, so nobody types "Non Reactve". */
const QUALITATIVE_OPTIONS: Partial<Record<LabDataType, string[]>> = {
  positive_negative: ['Negative', 'Positive'],
  reactive_nonreactive: ['Non-Reactive', 'Reactive'],
  normal_abnormal: ['Normal', 'Abnormal'],
};

export interface ResultEntryProps {
  /** An ORDER ITEM id — the sheet opens before any result row exists. */
  orderItemId: number | null;
  orderId?: number;
  onClose: () => void;
}

export default function ResultEntry({ orderItemId, orderId, onClose }: ResultEntryProps) {
  const { user } = useAuth();
  const { data: sheet, isLoading, error, refetch } = useResultSheet(orderItemId);
  const save = useSaveResult();
  const verify = useVerifyResult();
  const reopen = useReopenResult();

  const [draft, setDraft] = useState<Record<number, string>>({});
  const [notes, setNotes] = useState('');
  const [verifyComment, setVerifyComment] = useState('');
  const [reopenReason, setReopenReason] = useState('');
  const [showReopen, setShowReopen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Seed the draft from whatever is stored. Re-runs on every fresh sheet
  // (including the one returned by a save), so the inputs show exactly what
  // the server holds rather than a local copy that has quietly diverged.
  useEffect(() => {
    if (!sheet) return;
    const next: Record<number, string> = {};
    sheet.parameters.forEach((p) => { next[p.parameter_id] = p.value_text ?? ''; });
    setDraft(next);
    setNotes(sheet.result?.technician_comments ?? '');
    setActionError(null);
    setShowReopen(false);
    setReopenReason('');
  }, [sheet]);

  const canEnter = !!user?.modules?.includes('lab:result-entry');
  const canVerify = !!user?.modules?.includes('lab:verify');
  const canAmend = !!user?.modules?.includes('lab:amend');

  const status = sheet?.result?.status ?? 'pending';
  const itemStatus = sheet?.item.status;
  const locked = status === 'verified' || itemStatus === 'REPORTED';
  const beforeLab = !!itemStatus && !['ACCEPTED', 'PROCESSING', 'RESULT_ENTERED', 'VERIFIED', 'REPORTED'].includes(itemStatus);

  const dirty = useMemo(() => {
    if (!sheet) return false;
    if ((sheet.result?.technician_comments ?? '') !== notes) return true;
    return sheet.parameters.some((p) => (draft[p.parameter_id] ?? '') !== (p.value_text ?? ''));
  }, [sheet, draft, notes]);

  const filledCount = sheet ? sheet.parameters.filter((p) => (draft[p.parameter_id] ?? '').trim()).length : 0;

  const run = async (fn: () => Promise<unknown>) => {
    setActionError(null);
    try { await fn(); } catch (e) { setActionError((e as Error).message); }
  };

  const doSave = () => run(() => save.mutateAsync({
    orderItemId: orderItemId as number,
    orderId,
    input: {
      values: Object.entries(draft)
        // Only send what was actually typed. Sending empty strings would
        // overwrite a colleague's entry with blanks on a partial save.
        .filter(([, v]) => String(v).trim() !== '')
        .map(([parameter_id, value]) => ({ parameter_id: Number(parameter_id), value: String(value).trim() })),
      technician_comments: notes.trim() || null,
    },
  }));

  const input = (p: ResultParameter) => {
    const options = QUALITATIVE_OPTIONS[p.data_type];
    const value = draft[p.parameter_id] ?? '';
    const set = (v: string) => setDraft((d) => ({ ...d, [p.parameter_id]: v }));
    if (options) {
      return (
        <select
          className="h-8 w-full rounded-lg border border-border bg-card px-2 text-[13px]"
          value={value}
          disabled={locked || !canEnter}
          onChange={(e) => set(e.target.value)}
        >
          <option value="">—</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
          {/* A stored value the master no longer offers still displays. */}
          {value && !options.includes(value) && <option value={value}>{value}</option>}
        </select>
      );
    }
    return (
      <Input
        className="h-8 text-right font-mono"
        value={value}
        disabled={locked || !canEnter}
        placeholder={p.data_type === 'numeric' ? p.result_format ?? '' : 'Result'}
        onChange={(e) => set(e.target.value)}
      />
    );
  };

  return (
    <Dialog open={orderItemId !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl">
        {isLoading && <LoadingState label="Loading the result sheet…" />}
        {error && <ErrorState message={(error as Error).message} onRetry={() => refetch()} />}

        {sheet && (
          <>
            <DialogHeader>
              <DialogTitle className="flex flex-wrap items-center gap-2">
                {sheet.item.test_name}
                <span className="font-mono text-[12px] text-muted-foreground">{sheet.item.test_code}</span>
                <LabStatusBadge status={sheet.item.status} />
                {sheet.result?.has_critical && <Badge variant="danger">Critical</Badge>}
              </DialogTitle>
            </DialogHeader>

            <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Field label="Order">{sheet.item.order_number}</Field>
                <Field label="Method">{sheet.item.method ?? '—'}</Field>
                <Field label="Entered by">
                  {sheet.result?.entered_by ? `${sheet.result.entered_by} · ${fmtDateTime(sheet.result.entered_at)}` : '—'}
                </Field>
                <Field label="Verified by">
                  {sheet.result?.verified_by ? `${sheet.result.verified_by} · ${fmtDateTime(sheet.result.verified_at)}` : '—'}
                </Field>
              </div>

              {beforeLab && (
                <div className="rounded-lg border border-warn-soft bg-warn-soft px-3 py-2 text-[12px] text-warn-ink">
                  This test’s sample has not been received and accepted yet, so no result can be entered.
                  Receive the sample first.
                </div>
              )}

              {sheet.result?.status === 'sent_back' && sheet.result.sent_back_reason && (
                <div className="rounded-lg border border-warn-soft bg-warn-soft px-3 py-2 text-[12px] text-warn-ink">
                  <span className="font-semibold">Sent back for re-entry:</span> {sheet.result.sent_back_reason}
                </div>
              )}

              {locked && (
                <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-[12px]">
                  This result has been verified and can no longer be edited. Correcting it is a recorded
                  amendment, not an edit — see below.
                </div>
              )}

              {/* ── The parameter table ─────────────────────────── */}
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="px-3 py-2 text-left">Parameter</th>
                      <th className="px-3 py-2 text-right w-[130px]">Result</th>
                      <th className="px-3 py-2 text-left w-[80px]">Unit</th>
                      <th className="px-3 py-2 text-left w-[110px]">Reference</th>
                      <th className="px-3 py-2 text-left w-[120px]">Flag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sheet.parameters.map((p) => {
                      const changed = (draft[p.parameter_id] ?? '') !== (p.value_text ?? '');
                      return (
                        <tr key={p.parameter_id}
                          className={`border-b border-border last:border-0 ${isCriticalFlag(p.flag) ? 'bg-danger-soft/40' : ''}`}>
                          <td className="px-3 py-1.5">
                            {p.name}
                            <span className="ml-1 font-mono text-[10px] text-muted-foreground">{p.code}</span>
                          </td>
                          <td className="px-3 py-1.5">{input(p)}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{p.unit ?? '—'}</td>
                          {/*
                            The range shown here was resolved server-side for
                            this patient's age and sex before anything was
                            typed — the same selection the save will apply, so
                            what the technician was shown and what the value is
                            judged against cannot disagree.
                          */}
                          <td className="px-3 py-1.5 text-muted-foreground">{p.reference_display ?? '—'}</td>
                          <td className="px-3 py-1.5">
                            {changed
                              ? <span className="text-[11px] text-muted-foreground">on save</span>
                              : <AbnormalFlag flag={p.flag} />}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Technical note
                </label>
                <Textarea
                  rows={2}
                  disabled={locked || !canEnter}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Anything about the run itself — analyser, dilution, sample quality…"
                />
              </div>

              {sheet.result?.verification_comments && (
                <div className="rounded-lg border border-border px-3 py-2 text-[12px]">
                  <span className="font-semibold">Interpretation:</span> {sheet.result.verification_comments}
                </div>
              )}

              {(sheet.comments ?? []).length > 0 && (
                <div className="rounded-lg border border-border">
                  {(sheet.comments ?? []).map((c) => (
                    <div key={c.id} className="border-b border-border px-3 py-1.5 text-[12px] last:border-0">
                      <Badge variant="neutral">{c.comment_type}</Badge>{' '}
                      {c.comment}
                      <span className="text-muted-foreground"> — {c.created_by}, {fmtDateTime(c.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}

              {(sheet.verifications ?? []).length > 0 && (
                <div className="text-[11px] text-muted-foreground">
                  {(sheet.verifications ?? []).map((v) => (
                    <div key={v.id}>
                      {v.decision === 'verified' ? 'Verified' : 'Sent back'} by {v.decided_by} · {fmtDateTime(v.decided_at)}
                      {v.comments ? ` — ${v.comments}` : ''}
                    </div>
                  ))}
                </div>
              )}

              {/* ── Verification ────────────────────────────────── */}
              {canVerify && status === 'entered' && (
                <div className="rounded-xl border border-border p-3">
                  <h4 className="mb-1.5 text-sm font-semibold">Verification</h4>
                  <Textarea
                    rows={2}
                    value={verifyComment}
                    onChange={(e) => setVerifyComment(e.target.value)}
                    placeholder="Interpretation, or the reason if you are sending this back (required to send back)"
                  />
                  <div className="mt-2 flex gap-2">
                    <Button
                      variant="success"
                      size="sm"
                      disabled={verify.isPending || dirty}
                      title={dirty ? 'Save the edited values before verifying them' : undefined}
                      onClick={() => run(async () => {
                        await verify.mutateAsync({ orderItemId: orderItemId as number, orderId, decision: 'verified', comments: verifyComment.trim() || undefined });
                        setVerifyComment('');
                      })}
                    >
                      Verify result
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={verify.isPending || !verifyComment.trim()}
                      onClick={() => run(async () => {
                        await verify.mutateAsync({ orderItemId: orderItemId as number, orderId, decision: 'sent_back', comments: verifyComment.trim() });
                        setVerifyComment('');
                      })}
                    >
                      Send back for re-entry
                    </Button>
                  </div>
                  {dirty && (
                    <p className="mt-1 text-[11px] text-warn-ink">
                      There are unsaved changes — save them first, so what you verify is what is stored.
                    </p>
                  )}
                </div>
              )}

              {/* ── Amendment ───────────────────────────────────── */}
              {canAmend && locked && (
                <div className="rounded-xl border border-border p-3">
                  {!showReopen ? (
                    <Button variant="ghost" size="sm" onClick={() => setShowReopen(true)}>
                      Correct this verified result
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-[12px] text-muted-foreground">
                        This reopens the result for re-entry. The released report is not touched — once the
                        corrected values are entered and verified again, publishing an amendment creates a new
                        report version alongside the original.
                      </p>
                      <Textarea
                        rows={2}
                        value={reopenReason}
                        onChange={(e) => setReopenReason(e.target.value)}
                        placeholder="Why does this need correcting? (required, and kept on the record)"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          disabled={!reopenReason.trim() || reopen.isPending}
                          onClick={() => run(() => reopen.mutateAsync({ orderItemId: orderItemId as number, orderId, reason: reopenReason.trim() }))}
                        >
                          Reopen for correction
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setShowReopen(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {actionError && <ErrorState message={actionError} />}
            </div>

            <DialogFooter>
              <span className="mr-auto text-[11px] text-muted-foreground">
                {filledCount} of {sheet.parameters.length} parameters entered
              </span>
              <Button variant="ghost" onClick={onClose}>Close</Button>
              {canEnter && !locked && (
                <Button disabled={save.isPending || !dirty || beforeLab} onClick={doSave}>
                  {save.isPending ? 'Saving…' : 'Save result'}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
